<template>
  <div class="c-calendar" aria-live="polite" :aria-busy="isLoading">
    <div class="loader" v-if="isLoading">
      <i class="far fa-circle-notch fa-spin" aria-hidden="true"></i>
      Loading...
    </div>

    <section class="c-calendar__filters">
        <h2 class="u-hidden-visually">Event Filters</h2>
        <DateNavigation
            :next-button="false"
            :previous-button="false"
            :first-instance="firstInstance"
            :last-instance="lastInstance"
            :view-type="currentView" />
            
        <CheckBox v-if="freeEventsPresent && showFreeOnly == 'false'" name="showAllEvents" label="Show free events" 
            value="displayFreeEvents" :checked="showFreeEvents" 
            @checkboxEvent="(state) => filterFreeEvents(state.checked)"/>
    </section>

    <FullCalendar ref="fullCalendar" :options="calendarOptions" />
  </div>
</template>

<script>
import FullCalendar from "@fullcalendar/vue";
import momentPlugin from "@fullcalendar/moment";
import moment from "moment";
import listPlugin from "@fullcalendar/list";
import DateNavigation from "./DateNavigation";
import { EventBus } from "../event-bus";
import axios from "axios";
import CheckBox from "./CheckBox.vue";

export default {
  name: "Calendar",
  props: ["firstInstance", "lastInstance", "showFreeOnly"],
  components: {
    FullCalendar,
    DateNavigation,
    CheckBox,
  },
  data() {
    return {
      calendarOptions: {
        firstDay: 1,
        headerToolbar: {
          start: "title",
          center: "prev",
          end: "next",
        },
        dayMaxEventRows: true,
        views: {
          titleFormat: { year: 'numeric', month: 'short', day: '2-digit'},
        },
        footerToolbar: false,
        height: "auto",
        plugins: [listPlugin, momentPlugin],
        initialView: "listWeek",
        events: [],
        noEventsContent: 'No events to display in this week',
        displayEventTime: false,
        eventContent: this.eventContent,
        listDayFormat: "dddd DD MMMM",
        listDaySideFormat: false,
        fixedWeekCount: false,
        timeZone: "UTC",
        initialDate: this.firstInstance,
        validRange: this.validRange,
      },
      isLoading: true,
      allInstances: {},
      calendarApi: null,
      currentView: "listWeek",
      freeEventsPresent: false,
      showFreeEvents: true,
    };
  },
  mounted() {
    this.addEventListeners();
    this.calendarApi = this.$refs.fullCalendar.getApi();
    const isJumpToMonth = this.urlParamJumpMonth();
    // This check avoids requesting for events twice on jump to month requests
    if (!isJumpToMonth) {
      this.fetchEvents();
    }
  },
  created() {
    this.currentView = "listWeek"
  },
  methods: {
    /**
     * Check for free events in the in the displayed date range.
     */
    checkForFreeEvents()   {
        let currentDate = moment.utc(this.calendarApi.currentData.dateProfile.activeRange.start);
        const endDate = moment.utc(this.calendarApi.currentData.dateProfile.activeRange.end);
        this.freeEventsPresent = false;
        do    {
            // Checks for free events that are on that day (ignore time part).
            if (this.calendarOptions.events.some((event) => 
                    (event.isFDDFE || false) && event.start.startsWith(currentDate.format('YYYY-MM-DD')))
            )   {
                this.freeEventsPresent = true;
                break;
            }
            currentDate.add(1, 'day');
        }
        while(currentDate < endDate);
        
        // Ensure current display state is reflected in new display
        this.filterFreeEvents(this.showFreeEvents);
    },

    /**
     * Called when the display is changed (either through changing dates or toggle the display option) 
     * changes the display state to hide/show the free events.
     */
    filterFreeEvents(checked)  {
        this.showFreeEvents = checked;

        // Set display for events
        let currentDate = moment.utc(this.calendarApi.currentData.dateProfile.activeRange.start);
        const currentMonth = currentDate.format("YYYY-MM");
        // Reset event list to current months events
        let newEvents = [...this.allInstances[currentMonth] || []];
        // If excluding free events, filter them out
        if (checked === false)  {
            const endDate = moment.utc(this.calendarApi.currentData.dateProfile.activeRange.end);
            do    {
                const dateMatch = currentDate.format('YYYY-MM-DD');
                newEvents = newEvents.filter((event) => (event.isFDDFE === false) || event.start.startsWith(dateMatch) === false);
                currentDate.add(1, 'day');
            }
            while(currentDate <= endDate);
        }
        
        // Update calendar events which also updates the display.
        this.calendarOptions.events = [...newEvents];
    },

    addEventListeners() {
      EventBus.$on("gotoMonth", this.gotoMonth);

      this.nextAndPreviousButtonAdditionalEvents();
    },
    eventContent(arg) {
      return {
        html: arg.event.title,
      };
    },
    fetchEvents() {
        let currentDate = moment.utc(this.calendarApi.currentData.currentDate),
            currentMonth = currentDate.format("YYYY-MM");
        if (this.allInstances.hasOwnProperty(currentMonth)) {
            this.calendarOptions.events = this.allInstances[currentMonth]
            this.checkForFreeEvents();
        } else {

            this.isLoading = true;
            // Otherwise, fetch the instances
            let params = this.setupQueryParams();

            axios.get(window.wpAjax.url, {
                params: params,
            }).then((response) => {
                const instances = response.data;
                // Push our instances to the allInstances property, so we can use those next time rather than make another ajax call
                this.allInstances[currentMonth] = instances;

                // Assign the instances to our property as that is passed to the calendar
                this.calendarOptions.events = [...instances];
                this.isLoading = false;
                this.checkForFreeEvents();
            }).catch((error) => {
                this.isLoading = false;
                console.log(error);
            });
        }
    },
    gotoMonth(selectedMonth) {
      let month = moment(new Date(selectedMonth)).utc().startOf("month");
      this.calendarApi.gotoDate(month.format("YYYY-MM-DD"));
      this.fetchEvents();
      this.updateMonth(month.format("YYYY-MM-DD"), "gotoMonth");
    },
    nextAndPreviousButtonAdditionalEvents() {
      // previous button
      this.on("body", "click", ".fc-prev-button", (e) => {
        this.updateMonth(moment(this.calendarApi.currentData.currentDate).utc().format(), "prev");
        this.fetchEvents();
      });

      // next button
      this.on("body", "click", ".fc-next-button", (e) => {
        this.updateMonth(moment(this.calendarApi.currentData.currentDate).utc().format(), "next");
        this.fetchEvents();
      });
    },
    nextMonth() {
      this.calendarApi.next();
      this.updateMonth(this.calendarApi.currentData.currentDate, "next");
    },
    previousMonth() {
      this.calendarApi.prev();
      this.updateMonth(this.calendarApi.currentData.currentDate, "previous");
    },
    setupQueryParams() {
        const currentDate = moment.utc(this.calendarApi.currentData.currentDate);

        let params =  {
            action: "basethemeCalendarRequest",
            start: currentDate.format(),
            end: currentDate.endOf("month").endOf("isoWeek").format(),
        };

        if (this.showFreeOnly == 'true')    {
            params['show'] = 'free'
        }
        return params
    },
    urlParamJumpMonth() {
      const urlParams = new URLSearchParams(window.location.search);

      if (urlParams.has("month")) {
        const selectedMonth = urlParams.get("month");
        this.gotoMonth(selectedMonth);
        return true;
      }

      return false;
    },
    updateMonth(month, button) {
      let monthString = moment(new Date(month)).utc().startOf("month").format("YYYY-MM");
      EventBus.$emit("updateMonth", monthString, button);

      let url = new URL(window.location);
      url.searchParams.set("month", monthString);
      window.history.replaceState({}, "", url);
    },
    validRange(nowDate) {
      if (this.firstInstance && this.lastInstance) {
        return {
          start: this.firstInstance,
          end: this.lastInstance
        }
      }
      return {
        start: nowDate
      }
    },
    on(selector, eventType, childSelector, eventHandler) {
      const elements = document.querySelectorAll(selector);
      for (let element of elements) {
        element.addEventListener(eventType, (eventOnElement) => {
          if (eventOnElement.target.matches(childSelector)) {
            eventHandler(eventOnElement);
          }
        });
      }
    },
  },
};
</script>

<style lang='css'>
@import "~@fullcalendar/common/main.css";
@import "~@fullcalendar/list/main.css";
</style>
